import React, { useMemo } from 'react';
import { Table, Button } from 'react-bootstrap';
import StockItemComponent from './StockItemComponent'; // Assuming this component is defined elsewhere
import ManualStockItemCreationModal from './ManualStockItemCreationModal'; // Assuming this component is defined elsewhere
import ComponentStockItemsTable from './ComponentStockItemsTable';
import { buildStoragePlaceOptions } from '../../utils/storagePlace';

const StockItemTable = ({
  product,
  items,
  addNewItem,
  addStockItem,
  updateStockItems,
  updateProductStock,
  showBar,
  setTotalItemStock,
  updateProductLogs,
  userSelectList = [],
  isLoadingUsers = false,
  showHeader = true,
  onOpeningHistoryChange,
  useGlassTable = false,
  /** Section heading above the lots table. */
  headerLabel = 'Stock Items',
  /** Lab storage tree. When set, each lot can be placed on any storage node. */
  storageNodes = null,
  /** Shared place options from a parent table; built here when omitted. */
  storagePlaceOptions: sharedPlaceOptions = null,
  /**
   * Map destination. When set, each existing lot gets Place here.
   * @type {{ box: Object, slotIndex: number|null, slotRequired: boolean, onPlaced?: Function }|null}
   */
  placeHere = null,
}) => {
  // One shared options list for every Place cell — avoids rebuilding path
  // labels once per lot on every modal re-render.
  const storagePlaceOptions = useMemo(
    () =>
      sharedPlaceOptions ||
      (storageNodes ? buildStoragePlaceOptions(storageNodes) : null),
    [storageNodes, sharedPlaceOptions]
  );
  // plusIcon: The icon for adding a stock item
  const plusIcon = (
    <i className="fa fa-solid fa-plus" style={{ color: 'green' }}></i>
  );
  const sectionClassName = useGlassTable
    ? `stock-items-table-section stock-items-table-section--glass${
        showHeader ? '' : ' stock-items-table-section--glass-compact'
      }`
    : 'container mt-4';
  const tableClassName = useGlassTable
    ? 'stock-items-table stock-items-table--glass'
    : 'stock-items-table';
  const columnCount = storageNodes ? 12 : 11;

  return (
    <div className={sectionClassName}>
      {showHeader && (
        <h2 className={useGlassTable ? 'stock-items-table__title' : undefined}>
          {headerLabel}
        </h2>
      )}
      <Table striped bordered hover className={tableClassName}>
        <thead>
          <tr className="text-center">
            <th>#</th>
            <th>Order</th>
            <th className="stock-items-table__user-col">User</th>
            <th>Received</th>
            <th className="stock-items-table__user-col">Opened By</th>
            <th>Batch</th>
            <th>Expiry</th>
            <th>In Use?</th>
            <th>Opened On</th>
            {storageNodes ? <th>Place</th> : null}
            <th>Multi-Component</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {/* Sort items: first by opened status, then by received date */}
          {items
            .filter(item => item !== null && item !== undefined) // Filter out null/undefined items
            .sort((a, b) => {
              // First priority: items that are in use (opened)
              const aOpened = a.in_use ? 1 : 0;
              const bOpened = b.in_use ? 1 : 0;

              if (aOpened !== bOpened) {
                return bOpened - aOpened; // Opened items first
              }

              // Second priority: order by received date (first reception date)
              const aReceivedDate =
                a.order?.reception_dates && a.order.reception_dates.length > 0
                  ? new Date(a.order.reception_dates[0])
                  : new Date(0);
              const bReceivedDate =
                b.order?.reception_dates && b.order.reception_dates.length > 0
                  ? new Date(b.order.reception_dates[0])
                  : new Date(0);

              return bReceivedDate - aReceivedDate; // Most recent first
            })
            .map((item, index) => (
              <React.Fragment key={item.id}>
                <StockItemComponent
                  itemObj={item}
                  index={index}
                  editItem={true}
                  onSuccessfulSubmit={updateStockItems}
                  unitQuantity={product.unit_quantity}
                  onStockUpdate={updateProductStock}
                  productId={product.id}
                  showBar={showBar}
                  updateProductLogs={updateProductLogs}
                  product={product}
                  userSelectList={userSelectList}
                  isLoadingUsers={isLoadingUsers}
                  onOpeningHistoryChange={onOpeningHistoryChange}
                  useGlassTable={useGlassTable}
                  storageNodes={storageNodes}
                  storagePlaceOptions={storagePlaceOptions}
                  placeHere={placeHere}
                />
                {/* Component Table - Always visible for multi-component products */}
                {product?.supports_multi_components && (
                  <tr>
                    <td colSpan={columnCount} className="stock-items-table__component-cell">
                      <div className="stock-items-table__component-panel">
                        <h6 className="mb-3 stock-items-table__component-title">
                          <i className="fa fa-cubes me-2"></i>
                          Component Stock Items for Stock Item #{item.id}
                        </h6>
                          <ComponentStockItemsTable
                          stockItem={item}
                          product={product}
                          onRefresh={updateStockItems}
                          useGlassTable={useGlassTable}
                          storageNodes={storageNodes}
                          storagePlaceOptions={storagePlaceOptions}
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}

          {/* Render a new table row when the plus button at the bottom of the table is clicked */}
          {addNewItem && (
            <StockItemComponent
              productId={product.id}
              onSuccessfulSubmit={updateStockItems}
              showAddNewItem={addStockItem}
              currentStock={product.stock}
              updateProductLogs={updateProductLogs}
              product={product}
              userSelectList={userSelectList}
              isLoadingUsers={isLoadingUsers}
              onOpeningHistoryChange={onOpeningHistoryChange}
              useGlassTable={useGlassTable}
              storageNodes={storageNodes}
              storagePlaceOptions={storagePlaceOptions}
            />
          )}

          {/* If there are no items in the items array or if not in the process of adding a new item, render this row */}
          {items.length === 0 && !addNewItem ? (
            <tr className="text-center align-middle">
              <td colSpan={columnCount}>No items related to this product</td>
            </tr>
          ) : null}

          {/* Render the plus button to add a new item row (only if addStockItem is provided) */}
          {addStockItem && (
            <tr className="text-center align-middle">
              <td colSpan={columnCount}>
                <Button
                  variant={useGlassTable ? 'outline-success' : 'outline-light'}
                  onClick={addStockItem}
                >
                  {plusIcon}
                </Button>
              </td>
            </tr>
          )}

          {/* Render a modal for manual stock item creation (only if addStockItem is provided) */}
          {addStockItem && (
            <tr className="text-center align-middle">
              <td colSpan={columnCount}>
                <ManualStockItemCreationModal
                  productName={product.name}
                  productId={product.id}
                  productSubStock={product.quantity}
                  updateProductStock={updateProductStock}
                  onManualCreate={setTotalItemStock}
                  onSuccessfulSubmit={updateStockItems}
                  updateProductLogs={updateProductLogs}
                  product={product}
                  onOpeningHistoryChange={onOpeningHistoryChange}
                  storageNodes={storageNodes}
                  storagePlaceOptions={storagePlaceOptions}
                />
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default StockItemTable;
